import { CreateCheckoutRequest } from "@/@types/Checkouts/create-checkout";
import { AxiosErrorResponse } from "@/@types/Errors";
import CustomSelect from "@/components/shared/CustomSelect/CustomSelect";
import { Input } from "@/components/shared/Input/Input";
import { Button } from "@/components/ui/button";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "@/hooks/use-toast";
import { CheckoutsService } from "@/services/checkouts.service";
import useModalStore from "@/store/useModalStore";
import { normalizeUrlWhenPaste } from '@/utils/normalize';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Eye, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

interface CreateCheckoutProps {
  offers: {
    value: string;
    label: string;
  }[];
  modalName?: string;
}

export default function CreateCheckout({
  offers,
  modalName = 'funnel-create-checkout',
}: CreateCheckoutProps) {
  const { t } = useTranslation(['checkouts', 'common']);
  const { closeModal } = useModalStore();
  const { id } = useParams();

  const CreateOfferSchema = z.object({
    name: z.string().min(1, t('common:required_field')),
    offer_id: z.string().min(2, t('common:required_field')),
    back_redirect: z
      .string()
      .optional()
      .refine((value) => !value || /^https?:\/\/.+$/.test(value), {
        message: t('common:invalid_url'),
      }),
    is_active: z.boolean(),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
  } = useForm<CreateCheckoutRequest>({
    shouldUnregister: false,
    resolver: zodResolver(CreateOfferSchema),
    defaultValues: {
      name: '',
      offer_id: '0',
      is_active: true,
    },
  });

  const namePreview =
    watch('name')?.trim() || t('checkouts:name_preview_placeholder');

  const queryClient = useQueryClient();

  const { mutate, isPending: isCreatingCheckout } = useMutation({
    mutationFn: (data: CreateCheckoutRequest) => {
      if (!id) {
        throw new Error('Product id is required');
      }

      return CheckoutsService.createCheckout({ data, productId: id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`checkouts-${id}`] });
      toast({
        variant: 'success',
        description: t('checkouts:saved_checkout'),
      });
      closeModal(modalName);
    },
    onError: (error: AxiosError<AxiosErrorResponse>) => {
      toast({
        variant: 'error',
        description:
          error?.response?.data?.message || 'Ocorreu um erro ao enviar os dados.',
      });
    },
  });

  const onSubmit = async (data: CreateCheckoutRequest) => {
    mutate(data);
  };

  return (
    <section className='sm:overflow-y-hidden flex flex-col justify-between h-full'>
      <SheetHeader className='p-6' data-cy='modal-create-checkout'>
        <SheetTitle className='flex gap-2'>{t('create_checkout')} </SheetTitle>
      </SheetHeader>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className='flex flex-col justify-between h-full'
      >
        <section className='flex flex-col gap-4 px-6'>
          <div className='space-y-2'>
            <Input
              {...register('name')}
              label={t('common:name_single')}
              placeholder={t('checkouts:enter_checkout_name')}
              data-cy='input-checkout-name'
              helperText={errors.name?.message}
            />
            <p className='text-sm text-muted'>{t('checkouts:name_helper')}</p>
            <div className='flex items-start gap-2 rounded-md border border-colorBorder bg-[#09090B] px-3 py-2.5'>
              <Eye className='mt-0.5 h-4 w-4 shrink-0 text-muted' />
              <p className='text-sm leading-relaxed'>
                <span className='text-muted'>
                  {t('common:offerNameCheckoutPreview')}
                </span>{' '}
                <span className='font-medium text-interactive'>{namePreview}</span>
              </p>
            </div>
          </div>
          <CustomSelect
            options={offers}
            label={t('common:offer')}
            placeholder={t('common:select_offer')}
            dataCy='select-checkout-offer'
            onValueChange={(value) => {
              setValue('offer_id', value as any);
              clearErrors('offer_id');
            }}
            helperText={errors.offer_id?.message}
            hasError={!!errors.offer_id}
          />
          <Input
            {...register('back_redirect')}
            label={t('Back Redirect')}
            placeholder={t('checkouts:enter_back_redirect')}
            data-cy='input-checkout-back-redirect'
            helperText={errors.back_redirect?.message}
            onFocus={(e) => {
              if (!e.target.value) {
                e.target.value = 'https://';
                setValue('back_redirect', 'https://');
              }
            }}
            onPaste={(e) => {
              const finalValue = normalizeUrlWhenPaste(e);
              setValue('back_redirect', finalValue);
            }}
          />
        </section>
        <div className='flex border-t items-center justify-end gap-4 px-6 py-4 w-full max-sm:pb-12'>
          <div className='flex gap-2 justify-end items-end'>
            <Button
              variant='ghost'
              onClick={() => closeModal(modalName)}
              type='button'
            >
              {t('common:cancel')}
            </Button>
            <Button
              data-cy='btn-save-checkout'
              type='submit'
              disabled={isCreatingCheckout}
            >
              {isCreatingCheckout ? (
                <span className='flex items-center gap-2'>
                  <Loader2 className='animate-spin' />
                  {t('common:saving')}
                </span>
              ) : (
                t('checkouts:save_checkout')
              )}
            </Button>
          </div>
        </div>
      </form>
    </section>
  );
}
